// Transforms backend BoardLayout into the panel board's internal PanelLayout.
// This is the boundary between backend data and the panel_board frontend model.
// All edge-case handling, validation, and diagnostics are concentrated here.
// Pure function: no React imports, no API calls, no side effects.

import type { BoardLayout } from '@/types/api';
import type {
  PanelDevice,
  PanelLayout,
  PanelLayoutWarning,
  PanelRow,
  PanelSlot,
} from './types';

// BoardModule shape as seen from BoardLayout (inlined to avoid re-importing).
type RawModule = BoardLayout['modules'][number];

// Physical DIN-rail footprint (in modules) for device types whose real enclosure
// is wider than their pole count. The Spanish ICP/IGA (incoming breaker) ships in
// a sealed 3-module box, so it occupies 3 rail modules even though it is 2-pole.
// MCB/RCD physical width equals their pole count, so they are absent here and fall
// back to the backend modules_width. Mirrors rendering/layout_engine.py visual sizes.
const PHYSICAL_MODULE_WIDTH_BY_TYPE: Record<string, number> = {
  incoming_breaker: 3,
};

// Effective rail footprint: device-type override when present, else backend modules_width.
function physicalModulesWidth(deviceType: string, declaredWidth: number): number {
  return Math.max(declaredWidth, PHYSICAL_MODULE_WIDTH_BY_TYPE[deviceType] ?? 0);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function posKey(row: number, col: number): string {
  return `${row}:${col}`;
}

// Only add a warning once per (code, deviceId) pair to avoid noise.
function pushOnce(
  warnings: PanelLayoutWarning[],
  w: PanelLayoutWarning,
): void {
  const dup = warnings.some(
    x => x.code === w.code && x.deviceId === w.deviceId && x.position?.row === w.position?.row && x.position?.col === w.position?.col,
  );
  if (!dup) warnings.push(w);
}

// ─── Main ────────────────────────────────────────────────────────────────────

export function buildPanelLayout(layout: BoardLayout): PanelLayout {
  const warnings: PanelLayoutWarning[] = [];

  // ── Step 1: sort by (row_index, column_index) ascending ────────────────────
  // Guarantees anchor = first occurrence regardless of backend JSON order.
  const sorted = [...layout.modules].sort((a, b) =>
    a.row_index !== b.row_index
      ? a.row_index - b.row_index
      : a.column_index - b.column_index,
  );

  // ── Step 2: deduplicate overlapping slots ──────────────────────────────────
  const seenPositions = new Set<string>();
  const deduped: RawModule[] = [];
  for (const m of sorted) {
    const k = posKey(m.row_index, m.column_index);
    if (seenPositions.has(k)) {
      pushOnce(warnings, {
        code: 'DUPLICATE_SLOT',
        message: `Module "${m.module_id}" at (${m.row_index}, ${m.column_index}) duplicates an existing slot. Skipped.`,
        deviceId: m.device_id ?? undefined,
        position: { row: m.row_index, col: m.column_index },
      });
    } else {
      seenPositions.add(k);
      deduped.push(m);
    }
  }

  // ── Step 3: reject out-of-bounds modules ────────────────────────────────────
  const inBounds: RawModule[] = [];
  for (const m of deduped) {
    if (
      m.row_index < 0 || m.row_index >= layout.rows ||
      m.column_index < 0 || m.column_index >= layout.modules_per_row
    ) {
      pushOnce(warnings, {
        code: 'SLOT_OUT_OF_BOUNDS',
        message: `Module "${m.module_id}" at (${m.row_index}, ${m.column_index}) is outside grid ${layout.rows}×${layout.modules_per_row}. Skipped.`,
        deviceId: m.device_id ?? undefined,
        position: { row: m.row_index, col: m.column_index },
      });
    } else {
      inBounds.push(m);
    }
  }

  // ── Step 4: build fast lookups over clean data ──────────────────────────────
  const deviceById = new Map(layout.devices.map(d => [d.device_id, d]));

  // module_id → module (for anchor resolution)
  const moduleById = new Map<string, RawModule>();
  for (const m of inBounds) moduleById.set(m.module_id, m);

  // positional lookup for grid construction
  const moduleAt = new Map<string, RawModule>();
  for (const m of inBounds) moduleAt.set(posKey(m.row_index, m.column_index), m);

  // ── Step 5: detect orphan device_ids ───────────────────────────────────────
  for (const m of inBounds) {
    if (m.device_id && !m.is_reserve && !deviceById.has(m.device_id)) {
      pushOnce(warnings, {
        code: 'ORPHAN_DEVICE_ID',
        message: `Module "${m.module_id}" references device_id "${m.device_id}" not found in layout.devices. Slot rendered as reserve.`,
        deviceId: m.device_id,
        position: { row: m.row_index, col: m.column_index },
      });
    }
  }

  // ── Step 6: anchor detection — first slot (by sort order) per device_id ────
  const anchorModuleIdByDevice = new Map<string, string>(); // device_id → anchor module_id
  for (const m of inBounds) {
    if (m.device_id && !anchorModuleIdByDevice.has(m.device_id)) {
      anchorModuleIdByDevice.set(m.device_id, m.module_id);
    }
  }

  // anchor module object per device
  const anchorModuleByDevice = new Map<string, RawModule>();
  for (const [deviceId, moduleId] of anchorModuleIdByDevice) {
    const m = moduleById.get(moduleId);
    if (m) anchorModuleByDevice.set(deviceId, m);
  }

  // ── Step 7: slot count per device (for mismatch check) ─────────────────────
  const slotCountByDevice = new Map<string, number>();
  for (const m of inBounds) {
    if (m.device_id) {
      slotCountByDevice.set(m.device_id, (slotCountByDevice.get(m.device_id) ?? 0) + 1);
    }
  }

  // ── Step 8: diagnostics over devices ───────────────────────────────────────

  for (const [deviceId, device] of deviceById) {
    const actualCount = slotCountByDevice.get(deviceId) ?? 0;

    // Unplaced device
    if (actualCount === 0) {
      pushOnce(warnings, {
        code: 'UNPLACED_DEVICE',
        message: `Device "${deviceId}" (${device.device_type}) exists in layout.devices but has no BoardModule slots.`,
        deviceId,
      });
      continue;
    }

    // modules_width mismatch (only meaningful when device has slots)
    if (actualCount !== device.modules_width) {
      pushOnce(warnings, {
        code: 'MODULES_WIDTH_MISMATCH',
        message: `Device "${deviceId}" has modules_width=${device.modules_width} but ${actualCount} slot(s) found in BoardModule data.`,
        deviceId,
      });
    }
  }

  // ── Step 9: detect cross-row spanning ──────────────────────────────────────
  for (const m of inBounds) {
    if (!m.device_id || m.is_reserve) continue;
    const anchor = anchorModuleByDevice.get(m.device_id);
    if (!anchor || m.module_id === anchor.module_id) continue;
    if (m.row_index !== anchor.row_index) {
      pushOnce(warnings, {
        code: 'CROSS_ROW_DEVICE',
        message: `Device "${m.device_id}" anchor is at row ${anchor.row_index} but has continuation slot at row ${m.row_index}, col ${m.column_index}. Device rect is shown only within the anchor row.`,
        deviceId: m.device_id,
      });
    }
  }

  // ── Step 10: build PanelDevice for each known device ───────────────────────
  const panelDevices = new Map<string, PanelDevice>();
  for (const [deviceId, d] of deviceById) {
    const anchor = anchorModuleByDevice.get(deviceId);
    if (!anchor) continue; // unplaced — already warned
    panelDevices.set(deviceId, {
      deviceId: d.device_id,
      moduleId: anchor.module_id,
      deviceType: d.device_type,
      ratingA: d.rating_a,
      poleCount: d.pole_count,
      modulesWidth: physicalModulesWidth(d.device_type, d.modules_width),
      symbolId: d.symbol_id,
      rcdSensitivityMa: d.rcd_sensitivity_ma,
    });
  }

  // ── Step 11: build the 2-D grid ────────────────────────────────────────────
  const rows: PanelRow[] = [];
  for (let rowIdx = 0; rowIdx < layout.rows; rowIdx++) {
    const slots: PanelSlot[] = [];
    for (let colIdx = 0; colIdx < layout.modules_per_row; colIdx++) {
      const m = moduleAt.get(posKey(rowIdx, colIdx));

      if (!m) {
        // No module at this position → implicit empty cell.
        slots.push({ columnIndex: colIdx, kind: 'empty' });
        continue;
      }

      if (m.is_reserve || !m.device_id) {
        slots.push({ columnIndex: colIdx, kind: 'reserve' });
        continue;
      }

      // Orphan: treat as reserve so the grid stays intact.
      if (!deviceById.has(m.device_id)) {
        slots.push({ columnIndex: colIdx, kind: 'reserve' });
        continue;
      }

      const isAnchor = anchorModuleIdByDevice.get(m.device_id) === m.module_id;
      if (isAnchor) {
        slots.push({
          columnIndex: colIdx,
          kind: 'device_anchor',
          device: panelDevices.get(m.device_id),
        });
      } else {
        // Continuation slot within the same row: covered by the anchor's rect.
        slots.push({ columnIndex: colIdx, kind: 'device_continuation' });
      }
    }
    rows.push({ rowIndex: rowIdx, slots });
  }

  return {
    layoutId: layout.layout_id,
    modulesPerRow: layout.modules_per_row,
    rows,
    warnings,
  };
}
